import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Subscription } from "rxjs";
import { v4 as uuid } from "uuid";
import { MatDialog } from "@angular/material/dialog";
import { IPopup } from "../../interfaces/i-popup";
import { ImageGalleryService } from "../../services/internal-services/image-gallery/image-gallery.service";
import { PropertyStatus } from "../../model/property-status";
import { IPropertyResponse } from "../../interfaces/i-property-response";
import { GeoService } from "../../services/internal-services/geo.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { IDamageAssessmentResponse } from "../../interfaces/i-damage-assessment-response";
import { PropertyReportComponent } from "../../../dialog-components/property-report/property-report.component";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { Viewerlayer } from "../../model/viewerlayer";
import { ViewerConfigurationService } from "../../services/viewer-configuration.service";
import { ReportService } from "../../services/internal-services/report.service";
import { MapService } from "../../services/internal-services/map.service";
import { GeoJSONDatabaseService } from "../../services/internal-services/geojson-database.service";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";
import { IFootprintVisibility } from "../../interfaces/i-footprint-visibility";
import { PropertyDataService } from "../../services/internal-services/property-data.service";
import { ROOF_SCORE_MAP } from "../../model/roof-score.constants";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-property-location-info",
  templateUrl: "./property-location-info.component.html",
  styleUrls: ["./property-location-info.component.scss"],
  standalone: false,
})
export class PropertyLocationInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  subscriptions: Subscription[] = [];

  requestReport: boolean = false;

  type: string;

  featuresData: any = null;

  coordinates: number[];

  requestId: string;

  _aois: IProjectAOI[] = [];

  address: string;

  propertyStatus = PropertyStatus;

  /** Current measurement system, synced from ViewerService on init */
  measurementSystem: number = ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;

  /** Pre-built CSV string for the damage assessment copy button */
  damageCsvData: string | null = null;

  /** Property data once successfully loaded */
  propertyData: IPropertyResponse | null = null;

  /** Damage assessment data once successfully loaded */
  damageData: IDamageAssessmentResponse | null = null;

  /** Load status for the property info request */
  propertyLoadStatus: PropertyStatus | null = null;

  /** Load status for the damage assessment request */
  damageLoadStatus: PropertyStatus | null = null;

  /** Whether damage assessment is available for the current location (undefined = not yet checked) */
  isDamageEnabled: boolean | undefined = undefined;

  private _infoProperty: IPopup;

  private _layer: Viewerlayer;

  private _feature: any;

  private _enabledProperties: boolean = false;

  private _enabledPropertiesReport: boolean = false;

  @Input()
  autoRemove = true;

  /**
   * Visibility defaults provided by the parent manager.
   * While the component has not yet loaded data (no footprints on the map), it follows the
   * global state so all idle properties stay in sync. Once data is loaded and footprints are
   * on the map, the instance manages its own state independently.
   */
  @Input() set defaultVisibilityValues(value: IFootprintVisibility) {
    const hasLoaded =
      this.propertyLoadStatus === PropertyStatus.finished || this.damageLoadStatus === PropertyStatus.finished;
    if (!hasLoaded) {
      this._defaultVisibilityValues = { ...value };
      this.cdr.markForCheck();
    }
  }

  get defaultVisibilityValues(): IFootprintVisibility {
    return this._defaultVisibilityValues;
  }

  @Output()
  defaultVisibilityValuesChange = new EventEmitter<IFootprintVisibility>();

  private _defaultVisibilityValues: IFootprintVisibility = {
    propertyFootprint: true,
    labelsFootprint: true,
  };

  constructor(
    public googleAnalyticsService: GoogleAnalyticsService,
    public reportService: ReportService,
    public mapService: MapService,
    public galleryService: ImageGalleryService,
    private matDialog: MatDialog,
    public viewerConfigurationService: ViewerConfigurationService,
    private propertyDataService: PropertyDataService,
    public databaseService: GeoJSONDatabaseService,
    public cdr: ChangeDetectorRef,
  ) {}

  get layer(): Viewerlayer {
    return this._layer;
  }

  @Input() set layer(value: Viewerlayer) {
    this._layer = value;
  }

  get feature(): any {
    return this._feature;
  }

  @Input() set feature(value: any) {
    this._feature = value;
  }

  get infoProperty(): IPopup {
    return this._infoProperty;
  }

  @Input() set infoProperty(value: IPopup) {
    value.id = uuid();
    this._infoProperty = value;
    // Reset internal state when a new popup is loaded
    this.propertyData = null;
    this.damageData = null;
    this.propertyLoadStatus = null;
    this.damageLoadStatus = null;
    this.isDamageEnabled = undefined;
    this.loadComponent(value);
    // Restore cached data from the feature if already available (e.g. navigating CSV points)
    if (value.property) {
      this.handlePropertyResponse(value.property);
    }
    if (value.damageAssessment) {
      this.handleDamageResponse(value.damageAssessment);
    }
  }

  get enabledProperties(): boolean {
    return this._enabledProperties;
  }

  @Input() set enabledProperties(value: boolean) {
    this._enabledProperties = value;
  }

  get enabledPropertiesReport(): boolean {
    return this._enabledPropertiesReport;
  }

  @Input() set enabledPropertiesReport(value: boolean) {
    this._enabledPropertiesReport = value;
  }

  @Input()
  set aois(aois: IProjectAOI[]) {
    this._aois = aois;
    this.checkDamageAssessment();
  }

  @Input()
  set reportReady(ready: boolean) {
    if (ready && this.requestReport) {
      setTimeout(() => {
        const dialogConfig = this.getDialogConfig();
        this.matDialog.open(PropertyReportComponent, dialogConfig);
      }, 700);
      this.requestReport = false;
    }
  }

  @Output() onUpdatePropertyInfo = new EventEmitter<any>();

  @Output() onUpdateDamageInfo = new EventEmitter<any>();

  ngOnInit() {
    this.subscriptions.push(
      this.matDialog.afterAllClosed.subscribe(() => {
        this.cdr.detectChanges();
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }),
    );

    this.mapService.initDamagePropertyFootprintLayer();
    this.checkDamageAssessment();
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.viewerService.propertyInfoUnit$.subscribe((unit) => {
        if (unit !== null && unit !== undefined) {
          this.measurementSystem = unit;
        } else {
          this.subscriptions.push(
            this.viewerService.measurementSystemId$.subscribe((id) => {
              this.measurementSystem = id;
              this.cdr.markForCheck();
            }),
          );
        }
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  loadComponent(info: IPopup) {
    this.coordinates = info.coordinates;
    this.featuresData = info.featuresData;
    this.type = info.type;

    const infoProperties = this.viewerService.getInfoProperties();

    infoProperties.forEach((ip) => {
      ip.featuresData?.forEach((feature) => {
        feature.selected = ip.id === info.id;
      });
      ip.selected = ip.id === info.id;
    });

    this.viewerService.getCoreMap().removeRedPinMarkers();
    this.viewerService.getCoreMap().showRedPinMarker(this.coordinates, info.epsg, info.opts, info.id);
  }

  getAdditionalProperties() {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.GET_ELEMENTS_PROPERTY_INFO,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );

    const wkt = GeoService.pointToWkt([this.coordinates[0], this.coordinates[1]]);

    this.subscriptions.push(
      this.propertyDataService.fetchPropertyInfo(wkt).subscribe({
        next: (state) => {
          if (state.status === PropertyStatus.downloading) {
            this.propertyLoadStatus = PropertyStatus.downloading;
            this.propertyData = null;
          } else if (state.status === PropertyStatus.finished) {
            this.handlePropertyResponse(state.data);
          } else {
            this.propertyLoadStatus = PropertyStatus.error;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.propertyLoadStatus = PropertyStatus.error;
          this.cdr.detectChanges();
        },
      }),
    );
  }

  openPropertyDialog(): void {
    const location = {
      longitude: this.coordinates[0],
      latitude: this.coordinates[1],
    };
    if (
      this.viewerService.getCoreMap().getViewExtent().containsCoordinates(location.latitude, location.longitude) &&
      this.viewerService.getZoom() > ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL
    ) {
      this.viewerService.getCoreMap().setLocation(location);
      this.matDialog.open(PropertyReportComponent, this.getDialogConfig());
    } else {
      this.viewerService.setReportReady(false);
      this.requestReport = true;
      this.viewerService.getCoreMap().setLocation(location);
      this.viewerService.setZoom(ViewerConstants.DEFAULT_SEARCH_ZOOM);
    }

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.REQUEST_ELEMENTS_PROPERTY_OPEN_FORM,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  getDialogConfig() {
    return {
      width: "38%",
      height: "50%",
      id: "property-report-dialog",
      minWidth: "1175px",
      minHeight: "675px",
      autoFocus: false,
      data: {
        coordinates: this.coordinates,
        viewerService: this.viewerService,
        reportService: this.reportService,
        galleryService: this.galleryService,
        googleAnalyticsService: this.googleAnalyticsService,
        enableDamageAssessment: this.isDamageEnabled,
        aois: this._aois,
        address: this.address,
      },
    };
  }

  getDamageAssessment() {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.GET_ELEMENTS_DAMAGE_ASSESSMENT,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );

    const wkt = GeoService.pointToWkt([this.coordinates[0], this.coordinates[1]]);

    this.subscriptions.push(
      this.propertyDataService.fetchDamageAssessment(wkt).subscribe({
        next: (state) => {
          if (state.status === PropertyStatus.downloading) {
            this.damageLoadStatus = PropertyStatus.downloading;
            this.damageData = null;
          } else if (state.status === PropertyStatus.finished) {
            this.handleDamageResponse(state.data);
          } else {
            this.damageLoadStatus = PropertyStatus.error;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.damageLoadStatus = PropertyStatus.error;
          this.cdr.detectChanges();
        },
      }),
    );
  }

  handlePropertyResponse(response: IPropertyResponse) {
    if (response) {
      this.propertyData = response;
      this.propertyLoadStatus = PropertyStatus.finished;
      // Keep infoProperty.property set for base-viewer.service cleanup (feature_id)
      this.infoProperty.property = response;
      if (this.defaultVisibilityValues.propertyFootprint) this.showPropertyFootprint(response);
      if (this.defaultVisibilityValues.labelsFootprint) this.showPropertyLabels(response);
      this.onUpdatePropertyInfo.emit(response);
      this.databaseService.saveProperty(response).subscribe(() => {});
      this.cdr.detectChanges();
    }
  }

  handleDamageResponse(response: IDamageAssessmentResponse) {
    if (response) {
      this.damageData = response;
      this.damageLoadStatus = PropertyStatus.finished;
      // Keep infoProperty.damageAssessment set for base-viewer.service cleanup (feature_id)
      this.infoProperty.damageAssessment = response;
      this.damageCsvData = this.buildDamageCsv(response);
      if (this.defaultVisibilityValues.propertyFootprint) this.showDmgPropertyFootprint(response);
      if (this.defaultVisibilityValues.labelsFootprint) this.showDmgPropertyLabels(response);
      this.onUpdateDamageInfo.emit(response);
      this.databaseService.savePropertyDamageAssessment(response).subscribe(() => {});
      this.cdr.detectChanges();
    }
  }

  setAddress(address: string) {
    this.address = address;
  }

  getDamageAssessmentTooltip() {
    let tooltip: string;

    if (this.isDamageEnabled) {
      tooltip =
        "This feature provides access to damage assessment records. Learn more: " +
        this.viewerConfigurationService.getViewerConfiguration().damageAssessmentUrl;
    } else if (this.viewerService.getCoreMap().isDamageAssessmentAllowed()) {
      tooltip = "There is no damage assessment information for this location.";
    } else {
      tooltip =
        "This feature provides access to damage assessment records. To enable this for your organization. Learn more here: " +
        this.viewerConfigurationService.getViewerConfiguration().damageAssessmentUrl;
    }
    return tooltip;
  }

  /**
   * Check once for this coordinates if the location has enabled damage assessment
   * In the load component or the first time that we receive aois
   */
  checkDamageAssessment(): void {
    if (
      this.viewerService.collectionsPopulated &&
      this.isDamageEnabled === undefined &&
      this.infoProperty?.coordinates
    ) {
      if (this.viewerService.getCoreMap().isDamageAssessmentAllowed()) {
        const point = GeoService.toMercator(this.infoProperty.coordinates);
        const collectionManager = this.mapService.getCollectionManager();
        this.isDamageEnabled = this._aois?.some(
          (aoi) => aoi.allowDamageAssessment() && !!collectionManager.collectionIn(aoi.getName(), point),
        );
      } else {
        this.isDamageEnabled = false;
      }
    }
  }

  /**
   * Handles measurement system changes emitted by display components.
   * Persists the choice via ViewerService.
   */
  onMeasurementSystemChange(system: number): void {
    this.measurementSystem = system;
    this.viewerService.updatePropertyInfoUnit(system);
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    this.cdr.markForCheck();
  }

  /**
   * Handles zoom requests emitted by lib-property-overview.
   * Delegates to MapService to zoom the map to the requested feature.
   */
  onZoomRequested(event: { wkt: string; paddingPx: number }): void {
    this.mapService.zoomToPropertyFootprintWkt(event.wkt, event.paddingPx);
  }

  /**
   * Handles visibility changes for property footprint and labels.
   * Applies the relevant map footprint/label operations.
   */
  onPropertyVisibilityChange(values: IFootprintVisibility): void {
    const data = this.propertyData;
    if (values.propertyFootprint !== this._defaultVisibilityValues.propertyFootprint) {
      if (values.propertyFootprint) {
        this.showPropertyFootprint(data);
      } else {
        this.removePropertyFootprint(data);
      }
    }
    if (values.labelsFootprint !== this._defaultVisibilityValues.labelsFootprint) {
      if (values.labelsFootprint) {
        this.showPropertyLabels(data);
      } else {
        this.removePropertyLabels(data);
      }
    }
    this._defaultVisibilityValues = { ...values };
    this.defaultVisibilityValuesChange.emit(this._defaultVisibilityValues);
  }

  /**
   * Handles visibility changes from lib-damage-assessment-display.
   * Applies the relevant map damage footprint/label operations.
   */
  onDamageVisibilityChange(values: IFootprintVisibility): void {
    const data = this.damageData;
    if (values.propertyFootprint !== this._defaultVisibilityValues.propertyFootprint) {
      if (values.propertyFootprint) {
        this.showDmgPropertyFootprint(data);
      } else {
        this.removeDmgPropertyFootprint(data);
      }
    }
    if (values.labelsFootprint !== this._defaultVisibilityValues.labelsFootprint) {
      if (values.labelsFootprint) {
        this.showDmgPropertyLabels(data);
      } else {
        this.removeDmgPropertyLabels(data);
      }
    }
    this._defaultVisibilityValues = { ...values };
    this.defaultVisibilityValuesChange.emit(this._defaultVisibilityValues);
  }

  // ── Property footprint map management ─────────────────────────────────────

  /** Adds the property parcel and building footprints to the map. */
  private showPropertyFootprint(data: IPropertyResponse): void {
    if (data?.property_features?.parcel_wkt) {
      if (!this.mapService.getPropertyFootprintFeature(data.feature_id)) {
        data.feature_id = this.mapService.addPropertyFootprint(data.property_features.parcel_wkt, {
          layerRef: this._layer?.name,
          featureRef: this._feature?.id,
          footprintType: "parcel",
        });
      }
    }
    data?.structures
      ?.filter((s) => s.footPrint?.wkt)
      .forEach((structure) => {
        if (!this.mapService.getPropertyFootprintFeature(structure.feature_id)) {
          structure.feature_id = this.mapService.addPropertyFootprint(structure.footPrint.wkt, {
            layerRef: this._layer?.name,
            featureRef: this._feature?.id,
            footprintType: "building",
            color: this.getRoofScoreColor(structure.roof_condition?.value),
          });
        }
      });
  }

  /** Removes the property parcel and building footprints from the map. */
  private removePropertyFootprint(data: IPropertyResponse): void {
    if (data?.feature_id) {
      this.mapService.removePropertyFootprint(data.feature_id);
      data?.structures?.forEach((s) => {
        if (s.feature_id) this.mapService.removePropertyFootprint(s.feature_id);
      });
    }
  }

  /** Adds numbered building labels to the map for each structure. */
  private showPropertyLabels(data: IPropertyResponse): void {
    data?.structures
      ?.filter((s) => s.footPrint?.wkt)
      .forEach((structure, index) => {
        structure.label_id = this.mapService.addPropertyFootprint(structure.footPrint.wkt, {
          layerRef: this._layer?.name,
          featureRef: this._feature?.id,
          footprintType: "label",
          label: index + 1,
        });
      });
  }

  /** Removes building labels from the map. */
  private removePropertyLabels(data: IPropertyResponse): void {
    data?.structures?.forEach((s) => {
      if (s.label_id) this.mapService.removePropertyFootprint(s.label_id);
    });
  }

  // ── Damage footprint map management ───────────────────────────────────────

  /** Adds damage-coloured footprints (FEMA classification) to the map. */
  private showDmgPropertyFootprint(data: IDamageAssessmentResponse): void {
    const pre = data?.pre_catastrophe;
    if (pre?.property_features?.parcel_wkt) {
      pre.feature_id = this.mapService.addDamagePropertyFootprint(pre.property_features.parcel_wkt, {
        parcel: true,
        layerRef: this._layer?.name,
        featureRef: this._feature?.id,
      });
    }
    pre?.structure?.forEach((structure, index) => {
      const post = data?.post_catastrophe?.structure[index];
      if (!this.mapService.getDamagePropertyFootprintFeature(structure.feature_id)) {
        structure.feature_id = this.mapService.addDamagePropertyFootprint(structure.footPrint.wkt, {
          damage_level: post?.fema_classification,
          layerRef: this._layer?.name,
          featureRef: this._feature?.id,
        });
      }
    });
  }

  /** Removes damage footprints from the map. */
  private removeDmgPropertyFootprint(data: IDamageAssessmentResponse): void {
    const pre = data?.pre_catastrophe;
    if (pre?.feature_id) {
      this.mapService.removeDamagePropertyFootprint(pre.feature_id);
      pre?.structure?.forEach((s) => {
        if (s.feature_id) this.mapService.removeDamagePropertyFootprint(s.feature_id);
      });
    }
  }

  /** Adds numbered damage building labels to the map. */
  private showDmgPropertyLabels(data: IDamageAssessmentResponse): void {
    data?.pre_catastrophe?.structure?.forEach((structure, index) => {
      if (!this.mapService.getDamagePropertyFootprintFeature(structure.label_id)) {
        structure.label_id = this.mapService.addDamagePropertyFootprint(structure.footPrint.wkt, {
          layerRef: this._layer?.name,
          featureRef: this._feature?.id,
          footprintType: "label",
          label: index + 1,
        });
      }
    });
  }

  /** Removes damage building labels from the map. */
  private removeDmgPropertyLabels(data: IDamageAssessmentResponse): void {
    data?.pre_catastrophe?.structure?.forEach((s) => {
      if (s.label_id) this.mapService.removeDamagePropertyFootprint(s.label_id);
    });
  }

  // ── CSV builders ──────────────────────────────────────────────────────────

  /** Builds a simple CSV string from damage assessment data for the clipboard copy button. */
  private buildDamageCsv(data: IDamageAssessmentResponse): string {
    const headers =
      "Run on,Building Info,Catastrophe Score,FEMA Classification,Missing roof material,Roof structural damage,Tarp coverage,Debris coverage,Discoloration";
    const rows = data?.post_catastrophe?.structure
      .map((s) => {
        const date = s.source_image_metadata?.capture_date ?? "No capture date";
        const fema = s.fema_classification ?? "None - No damage";
        return `${date},${s.primaryStructure ? "Primary" : "Secondary"},${s.catastrophe_score ?? "-"}/100,${fema},${
          s.roof_condition?.missing_material_percent ?? "-"
        }%,${s.roof_condition?.structural_damage_percent ?? "-"}%,${s.roof_condition?.tarp_percent ?? "-"}%,${
          s.roof_condition?.debris_percent ?? "-"
        }%,${s.roof_condition?.discoloration?.percentage ?? "-"}%`;
      })
      .join("\n");
    return `${headers}\n${rows}`;
  }

  /** Returns the hex color for a given roof condition score (1–5). */
  getRoofScoreColor(value: number): string {
    return ROOF_SCORE_MAP[value]?.color ?? "#9e9e9e";
  }
}
